import axios from 'axios';
import { AuthToken, User, UserCreate, UserUpdate, Document, DocumentUpload, RequestForm, RequestFormCreate, EmployeeDirectoryItem, DirectoryListResponse, DirectoryFilters, DirectoryContactCreate, ConvertToUserRequest, UsefulLink, UsefulLinkCreate, UsefulLinkUpdate, UsefulLinksListResponse, CategoriesResponse, Location, LocationCreate, LocationUpdate, LocationsListResponse, PTORequest, PTORequestCreate, PTORequestReview, PTORequestListResponse, PTOBalance, PTOBalanceListResponse, PTOPolicy, PTOPolicyCreate, PTOPolicyListResponse, BlackoutDate, BlackoutDateCreate, NewsPost, NewsPostCreate, NewsPostUpdate, NewsPostListResponse, NewsPostPublish, NewsCategory, NewsCategoryCreate, NewsCategoryUpdate, VehicleInspection, VehicleInspectionCreate, VehicleInspectionUpdate, VehicleInspectionListResponse, VehicleInspectionExportResponse, Candidate, CandidateCreate, CandidateUpdate, CandidateListResponse, CandidateWithInterviews, CandidateInterview, CandidateInterviewCreate, CandidateInterviewUpdate, CandidateInterviewListResponse, PositionType, PositionTypeCreate, PositionTypeUpdate, PositionTypeListResponse, OnboardingChecklistItem, OnboardingChecklistItemCreate, OnboardingChecklistItemUpdate, OnboardingChecklistItemListResponse, OnboardingChecklistItemReorder, NewHire, NewHireCreate, NewHireUpdate, NewHireFromCandidate, NewHireListResponse, NewHireWithChecklist, NewHireChecklistEntryUpdate, HiringDashboardStats, CandidateStatus, CandidateSource, NewHireStatus } from '../types';

// Use relative URLs in production (empty string), localhost in development
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

export const api = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: async (username: string, password: string): Promise<AuthToken> => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);

    const response = await api.post('/auth/login', formData);
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },
};

// Users API
export const usersApi = {
  getMe: async (): Promise<User> => {
    const response = await api.get('/users/me');
    return response.data;
  },

  updateMe: async (data: UserUpdate): Promise<User> => {
    const response = await api.put('/users/me', data);
    return response.data;
  },

  getUsers: async (params?: {
    skip?: number;
    limit?: number;
    search?: string;
    role?: 'user' | 'manager';
    is_active?: boolean;
  }) => {
    const response = await api.get('/users/', { params });
    return response.data;
  },

  createUser: async (data: UserCreate): Promise<User> => {
    const response = await api.post('/users/', data);
    return response.data;
  },

  getUser: async (userId: number): Promise<User> => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },

  updateUser: async (userId: number, data: UserUpdate): Promise<User> => {
    const response = await api.put(`/users/${userId}`, data);
    return response.data;
  },

  deleteUser: async (userId: number): Promise<void> => {
    await api.delete(`/users/${userId}`);
  },

  changePassword: async (data: { current_password: string; new_password: string }): Promise<{ message: string }> => {
    const response = await api.post('/users/me/password', data);
    return response.data;
  },

  resetUserPassword: async (userId: number, data: { new_password: string }): Promise<{ message: string }> => {
    const response = await api.put(`/users/${userId}/password`, data);
    return response.data;
  },
};

// Documents API
export const documentsApi = {
  uploadDocument: async (file: File, metadata: DocumentUpload): Promise<Document> => {
    const formData = new FormData();
    formData.append('file', file);
    if (metadata.category) formData.append('category', metadata.category);
    if (metadata.description) formData.append('description', metadata.description);
    formData.append('is_public', metadata.is_public ? 'true' : 'false');

    const response = await api.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getDocuments: async (params?: {
    skip?: number;
    limit?: number;
    search?: string;
    category?: string;
  }) => {
    const response = await api.get('/documents/', { params });
    return response.data;
  },

  getDocument: async (documentId: number): Promise<Document> => {
    const response = await api.get(`/documents/${documentId}`);
    return response.data;
  },

  downloadDocument: async (documentId: number): Promise<Blob> => {
    const response = await api.get(`/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  updateDocument: async (documentId: number, data: Partial<DocumentUpload>): Promise<Document> => {
    const response = await api.put(`/documents/${documentId}`, data);
    return response.data;
  },

  deleteDocument: async (documentId: number): Promise<void> => {
    await api.delete(`/documents/${documentId}`);
  },

  getCategories: async (): Promise<string[]> => {
    const response = await api.get('/documents/categories/list');
    return response.data.categories;
  },

  emailDocument: async (documentId: number): Promise<{ message: string }> => {
    const response = await api.post(`/documents/${documentId}/email`);
    return response.data;
  },
};

// Request Forms API
export const requestFormsApi = {
  createRequestForm: async (data: RequestFormCreate): Promise<RequestForm> => {
    const response = await api.post('/api/request-forms/', data);
    return response.data;
  },

  getRequestForms: async (params?: {
    page?: number;
    page_size?: number;
  }) => {
    const response = await api.get('/api/request-forms/', { params });
    return response.data;
  },

  getRequestForm: async (requestId: number): Promise<RequestForm> => {
    const response = await api.get(`/api/request-forms/${requestId}`);
    return response.data;
  },

  deleteRequestForm: async (requestId: number): Promise<void> => {
    await api.delete(`/api/request-forms/${requestId}`);
  },
};

// Send Document API
export const sendDocumentApi = {
  sendDocument: async (file: File, name: string, subject?: string, message?: string): Promise<{ message: string; filename: string; size_kb: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    if (subject) formData.append('subject', subject);
    if (message) formData.append('message', message);

    const response = await api.post('/api/send-document/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Calendar API
export const calendarApi = {
  createEvent: async (eventData: {
    summary: string;
    description?: string;
    start_datetime: string;
    end_datetime: string;
    location?: string;
    timezone?: string;
  }): Promise<{ message: string; event_id: string; event_link: string; summary: string }> => {
    const response = await api.post('/api/calendar/events', eventData);
    return response.data;
  },

  checkStatus: async (): Promise<{ configured: boolean; message: string; calendar_name?: string }> => {
    const response = await api.get('/api/calendar/status');
    return response.data;
  },

  getEvents: async (timeMin?: string, timeMax?: string): Promise<{
    events: Array<{
      id: string;
      summary: string;
      description: string;
      location: string;
      start: { dateTime?: string; date?: string; timeZone?: string };
      end: { dateTime?: string; date?: string; timeZone?: string };
      htmlLink: string;
    }>;
    total: number;
  }> => {
    const params: Record<string, string> = {};
    if (timeMin) params.time_min = timeMin;
    if (timeMax) params.time_max = timeMax;
    const response = await api.get('/api/calendar/events', { params });
    return response.data;
  },
};

// Employee Directory API
export const directoryApi = {
  getDirectory: async (params?: {
    skip?: number;
    limit?: number;
    search?: string;
    department?: string;
    office_location?: string;
    job_title?: string;
    is_active?: boolean;
  }): Promise<DirectoryListResponse> => {
    const response = await api.get('/api/directory/', { params });
    return response.data;
  },

  getFilters: async (): Promise<DirectoryFilters> => {
    const response = await api.get('/api/directory/filters');
    return response.data;
  },

  getEmployee: async (userId: number): Promise<EmployeeDirectoryItem> => {
    const response = await api.get(`/api/directory/${userId}`);
    return response.data;
  },

  updateEmployee: async (userId: number, data: UserUpdate): Promise<EmployeeDirectoryItem> => {
    const response = await api.put(`/api/directory/${userId}`, data);
    return response.data;
  },

  deactivateEmployee: async (userId: number): Promise<void> => {
    await api.delete(`/api/directory/${userId}`);
  },

  createDirectoryContact: async (data: DirectoryContactCreate): Promise<EmployeeDirectoryItem> => {
    const response = await api.post('/api/directory/contact', data);
    return response.data;
  },

  convertToUser: async (userId: number, data: ConvertToUserRequest): Promise<EmployeeDirectoryItem> => {
    const response = await api.post(`/api/directory/${userId}/convert-to-user`, data);
    return response.data;
  },
};

// Useful Links API
export const linksApi = {
  getLinks: async (params?: {
    category?: string;
    search?: string;
    is_active?: boolean;
  }): Promise<UsefulLinksListResponse> => {
    const response = await api.get('/api/links/', { params });
    return response.data;
  },

  getCategories: async (): Promise<CategoriesResponse> => {
    const response = await api.get('/api/links/categories');
    return response.data;
  },

  getLink: async (linkId: number): Promise<UsefulLink> => {
    const response = await api.get(`/api/links/${linkId}`);
    return response.data;
  },

  createLink: async (data: UsefulLinkCreate): Promise<UsefulLink> => {
    const response = await api.post('/api/links/', data);
    return response.data;
  },

  updateLink: async (linkId: number, data: UsefulLinkUpdate): Promise<UsefulLink> => {
    const response = await api.put(`/api/links/${linkId}`, data);
    return response.data;
  },

  deleteLink: async (linkId: number, hardDelete: boolean = false): Promise<void> => {
    await api.delete(`/api/links/${linkId}`, {
      params: { hard_delete: hardDelete }
    });
  },

  trackClick: async (linkId: number): Promise<{ message: string; new_count: number }> => {
    const response = await api.post(`/api/links/${linkId}/click`);
    return response.data;
  },
};

// Locations API
export const locationsApi = {
  getLocations: async (params?: { is_active?: boolean }): Promise<LocationsListResponse> => {
    const response = await api.get('/api/locations/', { params });
    return response.data;
  },

  createLocation: async (data: LocationCreate): Promise<Location> => {
    const response = await api.post('/api/locations/', data);
    return response.data;
  },

  updateLocation: async (locationId: number, data: LocationUpdate): Promise<Location> => {
    const response = await api.put(`/api/locations/${locationId}`, data);
    return response.data;
  },

  deleteLocation: async (locationId: number): Promise<void> => {
    await api.delete(`/api/locations/${locationId}`);
  },
};

// PTO Requests API
export const ptoRequestsApi = {
  getRequests: async (params?: {
    user_id?: number;
    status?: string;
    pto_type?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
  }): Promise<PTORequestListResponse> => {
    const response = await api.get('/api/pto/requests/', { params });
    return response.data;
  },

  getRequest: async (requestId: number): Promise<PTORequest> => {
    const response = await api.get(`/api/pto/requests/${requestId}`);
    return response.data;
  },

  createRequest: async (data: PTORequestCreate): Promise<PTORequest> => {
    const response = await api.post('/api/pto/requests/', data);
    return response.data;
  },

  updateRequest: async (requestId: number, data: Partial<PTORequestCreate>): Promise<PTORequest> => {
    const response = await api.put(`/api/pto/requests/${requestId}`, data);
    return response.data;
  },

  reviewRequest: async (requestId: number, review: PTORequestReview): Promise<PTORequest> => {
    const response = await api.post(`/api/pto/requests/${requestId}/review`, review);
    return response.data;
  },

  cancelRequest: async (requestId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/pto/requests/${requestId}`);
    return response.data;
  },

  getPendingCount: async (): Promise<{ pending_count: number }> => {
    const response = await api.get('/api/pto/requests/pending/count');
    return response.data;
  },
};

// PTO Balances API
export const ptoBalancesApi = {
  getBalances: async (params?: {
    user_id?: number;
    policy_id?: number;
    year?: number;
  }): Promise<PTOBalanceListResponse> => {
    const response = await api.get('/api/pto/balances/', { params });
    return response.data;
  },

  getMyBalances: async (year?: number): Promise<PTOBalanceListResponse> => {
    const response = await api.get('/api/pto/balances/me', { params: { year } });
    return response.data;
  },

  getBalance: async (balanceId: number): Promise<PTOBalance> => {
    const response = await api.get(`/api/pto/balances/${balanceId}`);
    return response.data;
  },

  adjustBalance: async (balanceId: number, adjustment: {
    balance_id: number;
    adjustment_type: string;
    amount: number;
    reason: string;
  }): Promise<any> => {
    const response = await api.post(`/api/pto/balances/${balanceId}/adjust`, adjustment);
    return response.data;
  },

  initializeYear: async (year: number): Promise<{ message: string; created: number; skipped: number }> => {
    const response = await api.post(`/api/pto/balances/initialize-year/${year}`);
    return response.data;
  },
};

// PTO Policies API
export const ptoPoliciesApi = {
  getPolicies: async (params?: {
    is_active?: boolean;
    pto_type?: string;
  }): Promise<PTOPolicyListResponse> => {
    const response = await api.get('/api/pto/policies/', { params });
    return response.data;
  },

  getPolicy: async (policyId: number): Promise<PTOPolicy> => {
    const response = await api.get(`/api/pto/policies/${policyId}`);
    return response.data;
  },

  createPolicy: async (data: PTOPolicyCreate): Promise<PTOPolicy> => {
    const response = await api.post('/api/pto/policies/', data);
    return response.data;
  },

  updatePolicy: async (policyId: number, data: Partial<PTOPolicyCreate>): Promise<PTOPolicy> => {
    const response = await api.put(`/api/pto/policies/${policyId}`, data);
    return response.data;
  },

  deletePolicy: async (policyId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/pto/policies/${policyId}`);
    return response.data;
  },

  // Blackout Dates
  getBlackoutDates: async (params?: {
    is_active?: boolean;
    department?: string;
  }): Promise<{ blackout_dates: BlackoutDate[]; total: number }> => {
    const response = await api.get('/api/pto/policies/blackout-dates', { params });
    return response.data;
  },

  createBlackoutDate: async (data: BlackoutDateCreate): Promise<BlackoutDate> => {
    const response = await api.post('/api/pto/policies/blackout-dates', data);
    return response.data;
  },

  deleteBlackoutDate: async (blackoutId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/pto/policies/blackout-dates/${blackoutId}`);
    return response.data;
  },
};

// PTO Reports API
export const ptoReportsApi = {
  getSummary: async (params?: {
    start_date?: string;
    end_date?: string;
    department?: string;
  }): Promise<any> => {
    const response = await api.get('/api/pto/reports/summary', { params });
    return response.data;
  },

  getEmployeeUsage: async (params?: {
    year?: number;
    department?: string;
  }): Promise<{ employees: any[]; total: number; year: number }> => {
    const response = await api.get('/api/pto/reports/usage-by-employee', { params });
    return response.data;
  },

  getUpcomingAbsences: async (params?: {
    days_ahead?: number;
    department?: string;
  }): Promise<{ absences: any[]; total: number; start_date: string; end_date: string }> => {
    const response = await api.get('/api/pto/reports/upcoming-absences', { params });
    return response.data;
  },

  getTeamCalendar: async (params: {
    start_date: string;
    end_date: string;
    department?: string;
  }): Promise<{ events: any[]; total: number; start_date: string; end_date: string }> => {
    const response = await api.get('/api/pto/reports/team-calendar', { params });
    return response.data;
  },

  getMyHistory: async (year?: number): Promise<any> => {
    const response = await api.get('/api/pto/reports/my-history', { params: { year } });
    return response.data;
  },
};

// ==================== News API ====================
export const newsApi = {
  // News Posts
  getNewsPosts: async (params?: {
    status?: string;
    priority?: string;
    category_id?: number;
    search?: string;
    skip?: number;
    limit?: number;
    include_hidden?: boolean;
  }): Promise<NewsPostListResponse> => {
    const response = await api.get('/api/news/posts', { params });
    return response.data;
  },

  getNewsPost: async (postId: number): Promise<NewsPost> => {
    const response = await api.get(`/api/news/posts/${postId}`);
    return response.data;
  },

  createNewsPost: async (data: NewsPostCreate): Promise<NewsPost> => {
    const response = await api.post('/api/news/posts', data);
    return response.data;
  },

  updateNewsPost: async (postId: number, data: NewsPostUpdate): Promise<NewsPost> => {
    const response = await api.put(`/api/news/posts/${postId}`, data);
    return response.data;
  },

  deleteNewsPost: async (postId: number): Promise<void> => {
    await api.delete(`/api/news/posts/${postId}`);
  },

  publishNewsPost: async (postId: number, data?: NewsPostPublish): Promise<NewsPost> => {
    const response = await api.post(`/api/news/posts/${postId}/publish`, data || {});
    return response.data;
  },

  archiveNewsPost: async (postId: number): Promise<NewsPost> => {
    const response = await api.post(`/api/news/posts/${postId}/archive`);
    return response.data;
  },

  // News Categories
  getNewsCategories: async (): Promise<NewsCategory[]> => {
    const response = await api.get('/api/news/categories');
    return response.data;
  },

  createNewsCategory: async (data: NewsCategoryCreate): Promise<NewsCategory> => {
    const response = await api.post('/api/news/categories', data);
    return response.data;
  },

  updateNewsCategory: async (categoryId: number, data: NewsCategoryUpdate): Promise<NewsCategory> => {
    const response = await api.put(`/api/news/categories/${categoryId}`, data);
    return response.data;
  },

  deleteNewsCategory: async (categoryId: number): Promise<void> => {
    await api.delete(`/api/news/categories/${categoryId}`);
  },
};

// ==================== Vehicle Inspections API ====================
export const vehiclesApi = {
  // Vehicle Inspections
  getInspections: async (params?: {
    is_exported?: boolean;
    unit_type?: string;
    unit_number?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<VehicleInspectionListResponse> => {
    const response = await api.get('/api/vehicles/inspections', { params });
    return response.data;
  },

  getInspection: async (inspectionId: number): Promise<VehicleInspection> => {
    const response = await api.get(`/api/vehicles/inspections/${inspectionId}`);
    return response.data;
  },

  createInspection: async (data: VehicleInspectionCreate): Promise<VehicleInspection> => {
    const response = await api.post('/api/vehicles/inspections', data);
    return response.data;
  },

  updateInspection: async (inspectionId: number, data: VehicleInspectionUpdate): Promise<VehicleInspection> => {
    const response = await api.put(`/api/vehicles/inspections/${inspectionId}`, data);
    return response.data;
  },

  deleteInspection: async (inspectionId: number): Promise<void> => {
    await api.delete(`/api/vehicles/inspections/${inspectionId}`);
  },

  exportAndEmail: async (): Promise<VehicleInspectionExportResponse> => {
    const response = await api.post('/api/vehicles/inspections/export');
    return response.data;
  },
};

// ==================== Candidates API ====================
export const candidatesApi = {
  getCandidates: async (params?: {
    skip?: number;
    limit?: number;
    search?: string;
    status?: CandidateStatus;
    position?: string;
    source?: CandidateSource;
    location?: string;
    include_archived?: boolean;
  }): Promise<CandidateListResponse> => {
    const response = await api.get('/api/candidates/', { params });
    return response.data;
  },

  getCandidate: async (candidateId: number): Promise<CandidateWithInterviews> => {
    const response = await api.get(`/api/candidates/${candidateId}`);
    return response.data;
  },

  createCandidate: async (data: CandidateCreate): Promise<Candidate> => {
    const response = await api.post('/api/candidates/', data);
    return response.data;
  },

  updateCandidate: async (candidateId: number, data: CandidateUpdate): Promise<Candidate> => {
    const response = await api.put(`/api/candidates/${candidateId}`, data);
    return response.data;
  },

  deleteCandidate: async (candidateId: number): Promise<void> => {
    await api.delete(`/api/candidates/${candidateId}`);
  },

  archiveCandidate: async (candidateId: number, isArchived: boolean): Promise<Candidate> => {
    const response = await api.patch(`/api/candidates/${candidateId}/archive`, { is_archived: isArchived });
    return response.data;
  },

  uploadResume: async (candidateId: number, file: File): Promise<Candidate> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/api/candidates/${candidateId}/resume`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  downloadResume: async (candidateId: number): Promise<Blob> => {
    const response = await api.get(`/api/candidates/${candidateId}/resume`, {
      responseType: 'blob',
    });
    return response.data;
  },

  deleteResume: async (candidateId: number): Promise<void> => {
    await api.delete(`/api/candidates/${candidateId}/resume`);
  },

  getPositions: async (): Promise<string[]> => {
    const response = await api.get('/api/candidates/positions/list');
    return response.data;
  },

  getLocations: async (): Promise<string[]> => {
    const response = await api.get('/api/candidates/locations/list');
    return response.data;
  },

  getStats: async (): Promise<{ total_candidates: number; by_status: Record<string, number> }> => {
    const response = await api.get('/api/candidates/stats/summary');
    return response.data;
  },

  // Interviews
  getInterviews: async (candidateId: number): Promise<CandidateInterviewListResponse> => {
    const response = await api.get(`/api/candidates/${candidateId}/interviews`);
    return response.data;
  },

  createInterview: async (candidateId: number, data: CandidateInterviewCreate): Promise<CandidateInterview> => {
    const response = await api.post(`/api/candidates/${candidateId}/interviews`, data);
    return response.data;
  },

  updateInterview: async (interviewId: number, data: CandidateInterviewUpdate): Promise<CandidateInterview> => {
    const response = await api.put(`/api/candidates/interviews/${interviewId}`, data);
    return response.data;
  },

  deleteInterview: async (interviewId: number): Promise<void> => {
    await api.delete(`/api/candidates/interviews/${interviewId}`);
  },
};

// ==================== Onboarding API ====================
export const onboardingApi = {
  // Position Types
  getPositionTypes: async (params?: {
    include_inactive?: boolean;
  }): Promise<PositionTypeListResponse> => {
    const response = await api.get('/api/onboarding/position-types', { params });
    return response.data;
  },

  getPositionType: async (positionTypeId: number): Promise<PositionType> => {
    const response = await api.get(`/api/onboarding/position-types/${positionTypeId}`);
    return response.data;
  },

  createPositionType: async (data: PositionTypeCreate): Promise<PositionType> => {
    const response = await api.post('/api/onboarding/position-types', data);
    return response.data;
  },

  updatePositionType: async (positionTypeId: number, data: PositionTypeUpdate): Promise<PositionType> => {
    const response = await api.put(`/api/onboarding/position-types/${positionTypeId}`, data);
    return response.data;
  },

  deletePositionType: async (positionTypeId: number): Promise<void> => {
    await api.delete(`/api/onboarding/position-types/${positionTypeId}`);
  },

  // Checklist Items
  getChecklistItems: async (params?: {
    include_inactive?: boolean;
    category?: string;
    position_type_id?: number;
  }): Promise<OnboardingChecklistItemListResponse> => {
    const response = await api.get('/api/onboarding/checklist-items', { params });
    return response.data;
  },

  getChecklistItem: async (itemId: number): Promise<OnboardingChecklistItem> => {
    const response = await api.get(`/api/onboarding/checklist-items/${itemId}`);
    return response.data;
  },

  createChecklistItem: async (data: OnboardingChecklistItemCreate): Promise<OnboardingChecklistItem> => {
    const response = await api.post('/api/onboarding/checklist-items', data);
    return response.data;
  },

  updateChecklistItem: async (itemId: number, data: OnboardingChecklistItemUpdate): Promise<OnboardingChecklistItem> => {
    const response = await api.put(`/api/onboarding/checklist-items/${itemId}`, data);
    return response.data;
  },

  deleteChecklistItem: async (itemId: number): Promise<void> => {
    await api.delete(`/api/onboarding/checklist-items/${itemId}`);
  },

  reorderChecklistItems: async (data: OnboardingChecklistItemReorder): Promise<OnboardingChecklistItem[]> => {
    const response = await api.put('/api/onboarding/checklist-items/reorder', data);
    return response.data;
  },

  getCategories: async (): Promise<string[]> => {
    const response = await api.get('/api/onboarding/categories');
    return response.data;
  },
};

// ==================== New Hires API ====================
export const newHiresApi = {
  getNewHires: async (params?: {
    skip?: number;
    limit?: number;
    search?: string;
    status?: NewHireStatus;
    department?: string;
    manager_id?: number;
    include_archived?: boolean;
  }): Promise<NewHireListResponse> => {
    const response = await api.get('/api/new-hires/', { params });
    return response.data;
  },

  getNewHire: async (newHireId: number): Promise<NewHireWithChecklist> => {
    const response = await api.get(`/api/new-hires/${newHireId}`);
    return response.data;
  },

  createNewHire: async (data: NewHireCreate): Promise<NewHire> => {
    const response = await api.post('/api/new-hires/', data);
    return response.data;
  },

  createFromCandidate: async (data: NewHireFromCandidate): Promise<NewHire> => {
    const response = await api.post('/api/new-hires/from-candidate', data);
    return response.data;
  },

  updateNewHire: async (newHireId: number, data: NewHireUpdate): Promise<NewHire> => {
    const response = await api.put(`/api/new-hires/${newHireId}`, data);
    return response.data;
  },

  deleteNewHire: async (newHireId: number): Promise<void> => {
    await api.delete(`/api/new-hires/${newHireId}`);
  },

  archiveNewHire: async (newHireId: number, isArchived: boolean): Promise<NewHire> => {
    const response = await api.patch(`/api/new-hires/${newHireId}/archive`, { is_archived: isArchived });
    return response.data;
  },

  updateChecklistEntry: async (
    newHireId: number,
    entryId: number,
    data: NewHireChecklistEntryUpdate
  ): Promise<any> => {
    const response = await api.put(`/api/new-hires/${newHireId}/checklist/${entryId}`, data);
    return response.data;
  },

  getDashboardStats: async (): Promise<HiringDashboardStats> => {
    const response = await api.get('/api/new-hires/stats/dashboard');
    return response.data;
  },

  getDepartments: async (): Promise<string[]> => {
    const response = await api.get('/api/new-hires/departments/list');
    return response.data;
  },
};